export type LiveChatPageType =
  | "search"
  | "reviews"
  | "info"
  | "results"
  | "other";

type BusinessCandidate = {
  domain?: string | number;
  name?: string | number;
};

export type LiveChatBusinessContext = {
  callback_business_name: string;
  callback_business_public_id: string;
  callback_platform: "trustpilot" | "";
  callback_page_type: LiveChatPageType;
};

const MAX_CONTEXT_VALUE_LENGTH = 255;
const LIVECHAT_CONTEXT_SYNC_RETRY_DELAY_MS = 250;
const LIVECHAT_CONTEXT_SYNC_MAX_ATTEMPTS = 40;

type LiveChatWidget = {
  call?: (
    action: "update_session_variables",
    variables: LiveChatBusinessContext,
  ) => void;
};

type LiveChatBusinessContextSyncOptions = {
  context: LiveChatBusinessContext;
  getWidget: () => LiveChatWidget | undefined;
  isConsentGranted: () => boolean;
  onSyncError?: (error: unknown) => void;
  schedule?: (
    callback: () => void,
    delay: number,
  ) => ReturnType<typeof setTimeout>;
  cancelScheduled?: (timer: ReturnType<typeof setTimeout>) => void;
  maxAttempts?: number;
};

function sanitizeContextValue(value: unknown): string {
  if (typeof value !== "string" && typeof value !== "number") return "";

  return String(value)
    .replace(/[\u0000-\u001F\u007F]/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .slice(0, MAX_CONTEXT_VALUE_LENGTH);
}

function resolvePageType(pathname: string): LiveChatPageType {
  const segment = pathname.split("/").filter(Boolean).at(-1) ?? "";
  return ["search", "reviews", "info", "results"].includes(segment)
    ? (segment as LiveChatPageType)
    : "other";
}

export function buildLiveChatBusinessContext(
  pathname: string,
  business: BusinessCandidate | null | undefined,
): LiveChatBusinessContext {
  const safePathname = pathname.startsWith("/") ? pathname : "/";
  const pageType = resolvePageType(safePathname);
  const isFunnelPage = pageType !== "other";
  const selectedBusiness = isFunnelPage ? business : null;

  return {
    callback_business_name: sanitizeContextValue(selectedBusiness?.name),
    callback_business_public_id: sanitizeContextValue(selectedBusiness?.domain),
    callback_platform: isFunnelPage ? "trustpilot" : "",
    callback_page_type: pageType,
  };
}

export function startLiveChatBusinessContextSync({
  context,
  getWidget,
  isConsentGranted,
  onSyncError,
  schedule = setTimeout,
  cancelScheduled = clearTimeout,
  maxAttempts = LIVECHAT_CONTEXT_SYNC_MAX_ATTEMPTS,
}: LiveChatBusinessContextSyncOptions): () => void {
  let attempt = 0;
  let isCancelled = false;
  let timer: ReturnType<typeof setTimeout> | null = null;

  const sync = () => {
    if (isCancelled || !isConsentGranted()) return;

    const widget = getWidget();
    if (widget?.call) {
      try {
        widget.call("update_session_variables", context);
      } catch (error) {
        onSyncError?.(error);
      }
      return;
    }

    attempt += 1;
    if (attempt < maxAttempts) {
      timer = schedule(sync, LIVECHAT_CONTEXT_SYNC_RETRY_DELAY_MS);
    }
  };

  sync();

  return () => {
    isCancelled = true;
    if (timer !== null) cancelScheduled(timer);
  };
}
